// src/components/shop/ShopPopup.js
//
// The student's SHOP and CLOSET. Left: their avatar, big, wearing their
// outfit — and whatever item they tap, as a try-on. Right: the items by
// kind (Hats, Glasses, Wings, Pets, Frames) plus "My closet".
//
// Every item card says what will happen: Get free · Buy for 🪙 N · Wear ·
// Wearing ✓. Buying asks once ("Buy Royal Crown for 150 coins?"), then puts
// it on straight away. The coin counter counts down by itself — it is
// listening to the same wallet.
//
// Buying and wearing go through functions/shop.js; nothing here can change
// coins or ownership on its own.

import ModalPortal from "../common/ModalPortal";
import React, { useMemo, useState } from "react";
import AvatarWithOutfit from "../avatar/AvatarWithOutfit";
import AvatarMaker from "../avatar/AvatarMaker";
import { ItemArt, SLOT_LABELS, inSeason } from "../avatar/wearableArt";
import { SEASONS, nextOpenLabel, onSaleNow, seasonOf, seasonOpen } from "../avatar/seasonItems";
import { useSeasonLive } from "../avatar/seasons/seasonLive";
import CoinCounter, { Coin } from "../coins/CoinCounter";
import { useWallet } from "../../services/coinService";
import { buyShopItem, takeOffSlot, useMyCloset, useShopItems, wearShopItem } from "../../services/shopService";
import { useMyAvatar } from "../../services/avatarService";
import "./shop.css";

// Glasses, sunglasses, clothes and pins are sold in the avatar maker (they
// are drawn right onto the avatar), so the shop's "Dress up" tab opens it.
// (No "Pins" tab: pins are in the avatar maker, which has plenty.)
const TABS = ["dressup", "seasons", "hat", "necklace", "wings", "pet", "frame", "powerup", "closet"];
const SEASON_TABS = ["hat", "necklace", "wings", "pet", "frame"];
const TAB_ICONS = { dressup: "👕", seasons: "🎉", necklace: "📿", hat: "🎩", glasses: "🕶️", wings: "🪽", pet: "🐶", frame: "⭕", pin: "📍", powerup: "⚡", closet: "🧺" };
const MAX_POWERUPS = 9; // keep in step with functions/shop.js

// Everyone pays the same — educators too (their daily visits pay them coins).
// `educator` only hides power-ups, which are for students' Treasure Quest.
// `startTab` opens on a tab; `fromMaker` = opened from the avatar maker, so
// "Clothes, glasses & pins" just goes back to it.
const ShopPopup = ({ avatarUrl, onClose, educator = false, startTab = "hat", fromMaker = false }) => {
  useSeasonLive(); // the admin moved / opened / closed a season → redraw
  const itemPrice = (item) => Number(item?.price) || 0;
  const { items, byId, loaded, error } = useShopItems();
  const { owned, qty, outfit } = useMyCloset();
  const wallet = useWallet();
  const me = useMyAvatar();
  const [tab, setTab] = useState(startTab);
  const [maker, setMaker] = useState(false);
  const [tryOn, setTryOn] = useState(null);
  const [confirm, setConfirm] = useState(null); // item waiting for "yes"
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [cheer, setCheer] = useState(0);
  // "You need N more coins" — a big coin with the number floats up and fades
  const [shortFx, setShortFx] = useState(null); // { id, need, key }
  const showShort = (item, need) => {
    setShortFx({ id: item.id, need, key: Date.now() });
    setMsg({ tone: "short", text: `You need ${need} more coin${need === 1 ? "" : "s"} for the ${item.name}. Earn coins in games, songs, the daily trail and Treasure Quest — or ask a parent or teacher to add some.` });
  };

  // 🎉 Seasons: pick a holiday and the hats / wings / pets / frames become
  // that holiday's things. Look and try on any time; buy while it's on sale.
  const [season, setSeason] = useState(null);
  const [picking, setPicking] = useState(false);
  const saleOk = (i) => (i.builtInSeason ? onSaleNow(i) : inSeason(i));

  // Everyday tabs: everyday items. Seasonal items live in Seasons (and in
  // My closet once they're yours).
  const live = useMemo(() => items.filter((i) => i.active !== false && (!i.season || owned.has(i.id))), [items, owned]);
  const seasonal = useMemo(() => {
    if (!season) return [];
    const list = items.filter((i) => i.active !== false && i.season === season);
    // an admin-added item with the same name and kind as a built-in one
    // (the old Christmas starter set) would show twice — keep the built-in
    const builtIn = new Set(list.filter((i) => i.builtInSeason).map((i) => `${i.kind}:${String(i.name).toLowerCase()}`));
    return list.filter((i) => i.builtInSeason || owned.has(i.id) || !builtIn.has(`${i.kind}:${String(i.name).toLowerCase()}`));
  }, [items, season, owned]);
  const seasonKinds = SEASON_TABS.filter((k) => seasonal.some((i) => i.kind === k));
  const shown = useMemo(() => {
    if (tab === "closet") return items.filter((i) => owned.has(i.id) || (qty[i.id] || 0) > 0);
    if (season) return seasonal.filter((i) => i.kind === tab);
    return live.filter((i) => i.kind === tab && !i.season);
  }, [items, live, seasonal, season, tab, owned, qty]);

  const wearing = (item) => outfit?.[item.kind] === item.id;

  const act = async (item) => {
    setMsg(null);
    const need = itemPrice(item) - (Number(wallet.coins) || 0);
    if (!owned.has(item.id) && need > 0 && !(item.kind === "powerup" && (qty[item.id] || 0) >= MAX_POWERUPS)) {
      showShort(item, need);
      return;
    }
    if (item.kind === "powerup") {
      // used up in Treasure Quest — buy again any time; asks once
      if (confirm?.id !== item.id) {
        setConfirm(item);
        return;
      }
      setBusy(item.id);
      setConfirm(null);
      try {
        const out = await buyShopItem(item.id);
        setCheer((c) => c + 1);
        setMsg({ tone: "ok", text: `You got a ${item.name}! You have ${out?.qty ?? "more"} now. ⚡` });
      } catch (e) {
        setMsg({ tone: "bad", text: e.message });
      } finally {
        setBusy("");
      }
      return;
    }
    if (wearing(item)) {
      setBusy(item.id);
      try {
        await takeOffSlot(item.kind);
        setTryOn(null);
      } catch (e) {
        setMsg({ tone: "bad", text: e.message });
      } finally {
        setBusy("");
      }
      return;
    }
    if (!owned.has(item.id) && itemPrice(item) > 0 && confirm?.id !== item.id) {
      setTryOn(item);
      setConfirm(item);
      return;
    }
    setBusy(item.id);
    setConfirm(null);
    try {
      if (!owned.has(item.id)) {
        await buyShopItem(item.id);
        setCheer((c) => c + 1);
        setMsg({ tone: "ok", text: itemPrice(item) > 0 ? `You bought the ${item.name}! 🎉` : `You got the ${item.name}! 🎉` });
      }
      await wearShopItem(item.id);
      setTryOn(null);
    } catch (e) {
      setMsg({ tone: "bad", text: e.message });
    } finally {
      setBusy("");
    }
  };

  const buttonFor = (item) => {
    const price = itemPrice(item);
    if (busy === item.id) return { text: "", cls: "is-busy", spin: true };
    if (item.kind === "powerup") {
      if ((qty[item.id] || 0) >= MAX_POWERUPS) return { text: "Bag full!", cls: "is-short", disabled: true };
      if (wallet.coins < price) return { text: `Need ${price - wallet.coins} more`, cls: "is-short" };
      if (confirm?.id === item.id) return { text: "Yes, buy it!", cls: "is-confirm" };
      return { text: "Buy", cls: "is-buy", coin: price };
    }
    if (wearing(item)) return { text: "Take off", cls: "is-wearing" };
    if (owned.has(item.id)) return { text: "Wear", cls: "is-owned" };
    if (item.season && !saleOk(item)) return { text: item.builtInSeason ? nextOpenLabel(item.season) : "Not on sale now", cls: "is-later", disabled: true };
    if (price === 0) return { text: "Get free", cls: "is-free" };
    if (wallet.coins < price) return { text: `Need ${price - wallet.coins} more`, cls: "is-short" };
    if (confirm?.id === item.id) return { text: "Yes, buy it!", cls: "is-confirm" };
    return { text: "Buy", cls: "is-buy", coin: price };
  };

  return (
    <ModalPortal>
      <div className="sbl-shop-pop" role="dialog" aria-label="Shop" onClick={onClose}>
        <div className="sbl-shop" onClick={(e) => e.stopPropagation()}>
          <header className="sbl-shop-head">
            <h2>🛍 Shop</h2>
            <CoinCounter size="lg" />
            <button type="button" className="sbl-shop-x" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          <div className="sbl-shop-body">
            <aside className="sbl-shop-me">
              <div className={`sbl-shop-stage ${cheer ? "is-cheer" : ""}`} key={cheer}>
                <AvatarWithOutfit look={me.look} avatarUrl={avatarUrl || me.avatarUrl} outfit={outfit} tryOn={tryOn} size={130} items={byId} />
                {cheer > 0 && (
                  <span className="sbl-shop-sparkles" aria-hidden="true">
                    {Array.from({ length: 10 }, (_, i) => (
                      <i key={i} style={{ "--a": `${i * 36}deg` }} />
                    ))}
                  </span>
                )}
              </div>
              {tryOn?.kind === "powerup" ? (
                <p className="sbl-shop-note">
                  <b>{tryOn.name}</b> —{" "}
                  {tryOn.effect === "moves" ? "5 more moves when you're about to run out." : tryOn.effect === "hammer" ? "smash any one gem you choose." : "mix up the whole board."}
                </p>
              ) : tryOn && !owned.has(tryOn.id) ? (
                <p className="sbl-shop-note">Trying on the <b>{tryOn.name}</b></p>
              ) : (
                <p className="sbl-shop-note">Tap anything to try it on!</p>
              )}
              {confirm && (
                <div className="sbl-shop-confirm">
                  Buy the <b>{confirm.name}</b> for <Coin size={18} /> <b>{itemPrice(confirm)}</b>?
                  <span>
                    <button type="button" onClick={() => act(confirm)}>Yes!</button>
                    <button
                      type="button"
                      className="is-no"
                      onClick={() => {
                        setConfirm(null);
                        setTryOn(null);
                      }}
                    >
                      Not now
                    </button>
                  </span>
                </div>
              )}
              {msg && <p className={`sbl-shop-msg is-${msg.tone}`}>{msg.text}</p>}
            </aside>

            <section className="sbl-shop-items">
              <nav className="sbl-shop-tabs">
                {TABS.filter((t) => {
                  if (educator && t === "powerup") return false;
                  if (season) return t === "seasons" || t === "dressup" || seasonKinds.includes(t);
                  if (t === "necklace") return live.some((i) => i.kind === "necklace" && !i.season);
                  return true;
                }).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`${!picking && tab === t ? "is-on" : ""} ${t === "dressup" ? "is-dressup" : ""} ${t === "seasons" ? `is-holiday ${picking || season ? "is-on" : ""}` : ""}`}
                    onClick={() => {
                      if (t === "dressup") return fromMaker ? onClose() : setMaker(true);
                      if (t === "seasons") return setPicking((v) => !v);
                      setPicking(false);
                      return setTab(t);
                    }}
                  >
                    <span aria-hidden="true">{TAB_ICONS[t]}</span>
                    {t === "closet" ? "My closet" : t === "dressup" ? "Clothes, glasses & pins" : t === "seasons" ? "Seasons" : SLOT_LABELS[t]}
                  </button>
                ))}
              </nav>
              {season && !picking && (
                <div className="sbl-shop-season-bar" style={{ "--s1": seasonOf(season)?.palette.main, "--s2": seasonOf(season)?.palette.second }}>
                  <span>
                    {seasonOf(season)?.emoji} <b>{seasonOf(season)?.name}</b> · {seasonOpen(season) ? "on sale now!" : `try things on now — ${nextOpenLabel(season).replace(/^On /, "on ")}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSeason(null);
                      setTab("hat");
                    }}
                  >
                    ✕ Leave season
                  </button>
                </div>
              )}
              {picking && (
                <div className="sbl-shop-seasons">
                  <p>Pick a holiday — the hats, wings, pets and frames turn into that holiday's things. Try them on any time!</p>
                  <div className="sbl-shop-seasons-grid">
                    {SEASONS.map((x) => (
                      <button
                        key={x.id}
                        type="button"
                        className={`sbl-shop-season ${seasonOpen(x.id) ? "is-open" : ""}`}
                        style={{ "--s1": x.palette.main, "--s2": x.palette.second }}
                        onClick={() => {
                          setSeason(x.id);
                          setPicking(false);
                          setTab("hat");
                          setTryOn(null);
                          setConfirm(null);
                        }}
                      >
                        <span aria-hidden="true">{x.emoji}</span>
                        <b>{x.name}</b>
                        <small>{seasonOpen(x.id) ? "On sale now!" : nextOpenLabel(x.id)}</small>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {picking ? null : !loaded ? (
                <p className="sbl-shop-empty">Opening the shop…</p>
              ) : error ? (
                <p className="sbl-shop-empty">The shop couldn’t open right now. Try again soon!</p>
              ) : shown.length === 0 ? (
                <p className="sbl-shop-empty">
                  {tab === "closet" ? "Nothing here yet — get something free or save up your coins!" : "Nothing here yet. Check back soon!"}
                </p>
              ) : (
                <div className="sbl-shop-grid">
                  {shown.map((item) => {
                    const b = buttonFor(item);
                    const price = itemPrice(item);
                    return (
                      <div
                        key={item.id}
                        className={`sbl-shop-card ${wearing(item) ? "is-wearing" : ""} ${tryOn?.id === item.id ? "is-trying" : ""}`}
                        onClick={() => {
                          setTryOn(item);
                          if (confirm && confirm.id !== item.id) setConfirm(null);
                        }}
                      >
                        <span className={`sbl-shop-tag ${owned.has(item.id) ? "is-owned" : price === 0 ? "is-free" : ""}`}>
                          {item.kind === "powerup" && tab === "closet" ? `× ${qty[item.id] || 0}` : owned.has(item.id) ? "Yours" : price === 0 ? "FREE" : (
                            <>
                              <Coin size={14} /> {price}
                            </>
                          )}
                        </span>
                        <span className={`sbl-shop-art sbl-shop-art-${item.kind}`}>
                          <ItemArt item={item} />
                        </span>
                        <span className="sbl-shop-name">{item.name}</span>
                        {item.kind === "powerup" && (
                          <span className="sbl-shop-have">
                            {qty[item.id] ? `You have ${qty[item.id]}` : "Use in Treasure Quest"}
                          </span>
                        )}
                        {busy === item.id && (
                          <span className="sbl-shop-card-busy" aria-label="Loading">
                            <span className="sbl-spinner" />
                          </span>
                        )}
                        {shortFx?.id === item.id && (
                          <span key={shortFx.key} className="sbl-shop-short" aria-hidden="true">
                            <Coin size={44} />
                            <b>{shortFx.need} more</b>
                          </span>
                        )}
                        <button
                          type="button"
                          className={`sbl-shop-btn ${b.cls}`}
                          disabled={b.disabled || Boolean(busy)}
                          onClick={(e) => {
                            e.stopPropagation();
                            act(item);
                          }}
                        >
                          {b.spin ? <span className="sbl-spinner is-small" aria-label="Loading" /> : b.text}
                          {b.coin ? (
                            <>
                              {" "}
                              <Coin size={15} /> {b.coin}
                            </>
                          ) : null}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
        {maker && (
          <div onClick={(e) => e.stopPropagation()}>
            <AvatarMaker isEducator={educator} fromShop onClose={() => setMaker(false)} />
          </div>
        )}
      </div>
    </ModalPortal>
  );
};

export default ShopPopup;
